import { ArrowRightIcon } from "lucide-react"
import type * as React from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { useCan } from "@/features/auth/auth-context"
import { EwayPending } from "@/features/invoices/EwayPending"
import { ReportCustomerTable } from "@/features/reports/ReportCustomerTable"
import { ReportExportCard } from "@/features/reports/ReportExportCard"
import {
  REPORT_PRESET_LABEL_KEY,
  isFeatureOff,
  useOutstandingAgeing,
  useReportCustomers,
  useReportMonthlyBilled,
  useReportPeriod,
  useReportSummary,
} from "@/features/reports/use-reports"
import { ChartBilledByMonth } from "@/components/chart-billed-by-month"
import { ChartOutstandingByAge } from "@/components/chart-outstanding-by-age"
import { ChartTopCustomers } from "@/components/chart-top-customers"
import { SyncStatus } from "@/components/SyncStatus"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { ApiError } from "@/lib/api-error"
import { useFormatDay } from "@/lib/dates"
import { REPORT_PERIOD_PRESETS, type ReportSummaryResponse } from "@/lib/endpoints"
import { formatRupees } from "@/lib/money"
import { routes } from "@/routes/paths"

/**
 * Reports — §11.2's ONE permitted secondary screen, and §17.4's CA export.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WHAT THIS SCREEN IS: "period selector (this month / last month / this FY)
 * showing total billed, tax collected, and invoice count, plus a customer-wise
 * summary and an export button wired to §17.4. This screen exists mainly to feed
 * the CA."
 *
 * CHARTS ARE ALLOWED HERE AND ARE NOT ALLOWED ON HOME, and that is not a
 * contradiction. §11.2's "do not build a separate analytics page with charts"
 * governs the HOME screen — it is about what competes with the four action tiles
 * for the space above the fold, and the one chart it grants there is already
 * spent on billed-by-month. The three here each answer a question the owner acts
 * on: who to phone this morning, where the money comes from, and how the year is
 * running. There is deliberately NO pie chart; see `chart-top-customers.tsx`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE CAPTIONS ARE THE HARD PART, NOT THE LAYOUT. Four document types and a
 * payments ledger now exist and each belongs in a different column; a figure
 * with the wrong caption produces a report that looks right and FILES WRONG.
 * What this screen promises, in the words on it:
 *
 *   "Total billed" and "Tax on these bills" are NET OF CREDIT NOTES (§17.3), and
 *     a credit note counts in the month IT was issued — which is the month its
 *     CDNR reduction is declared in, even when the bill it corrects is older.
 *     That can make a month credit more than it billed, so the net can be
 *     NEGATIVE; the screen says why rather than flooring it at zero, because a
 *     floor would hide a real reduction and "−₹2,340" with no explanation reads
 *     as broken software.
 *   "Money received" is CASH, and never shares a caption with a billed figure.
 *     Billed in August and received in August are different questions and in this
 *     trade the answers usually differ. It is absent — never zero — when the
 *     payments module is off.
 *   CANCELLED and DRAFT bills contribute nothing and are COUNTED SEPARATELY, so
 *     the register the CA is about to receive has more rows than the total
 *     accounts for and he knows it before he opens it.
 *   DELIVERY CHALLANS reach NO rupee figure on this screen. They create no
 *     liability; only their serial range is declared, in the export.
 *   DISCOUNTS (§15(3)) have ALREADY reduced the taxable value, so they are shown
 *     for context and never subtracted a second time.
 *
 * EVERY RUPEE GOES THROUGH `formatRupees`, INCLUDING IN THE CHARTS' TOOLTIPS AND
 * THEIR BUTTON ROWS. None of the three charts draws a rupee axis — a rupee tick
 * is a figure shown to a human and would need Indian grouping too, and
 * abbreviating it to "12.8L" would mean a second money formatter outside
 * `packages/tax`.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function ReportsScreen(): React.JSX.Element {
  const { t } = useTranslation()
  const formatDay = useFormatDay()
  const { preset, setPreset } = useReportPeriod()

  /*
   * THE CAPABILITY GATE ON THE AGEING REQUEST, not a convenience. Without
   * `payment.view` the request is never made: the server would refuse it, and
   * asking would only write a denial into the audit log (§2.1 audits denials).
   * The module flag is the server's half and arrives as a 404.
   */
  const canSeePayments = useCan("payment.view")

  const summaryQuery = useReportSummary(preset)
  const customersQuery = useReportCustomers(preset)
  const ageingQuery = useOutstandingAgeing({ enabled: canSeePayments })
  const monthlyQuery = useReportMonthlyBilled(summaryQuery.data?.financialYear.label)

  const summary = summaryQuery.data

  return (
    <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t("nav.reports")}</h1>
          <p className="text-muted-foreground text-sm">{t("reports.subtitle")}</p>
        </div>
        <SyncStatus
          updatedAt={summaryQuery.dataUpdatedAt}
          syncing={summaryQuery.isFetching}
          onSync={() => {
            void summaryQuery.refetch()
          }}
        />
      </div>

      {/*
        * §17.7's WORK QUEUE — the bills still waiting for an e-way bill number,
        * and the one thing on this screen that is a JOB rather than a figure.
        *
        * ABOVE THE PERIOD SELECTOR, DELIBERATELY. Everything below answers "what
        * happened between two dates"; a consignment waiting for paper does not
        * stop waiting because the selector says "last month", and placing it
        * under the dates would invite exactly that misreading.
        *
        * It renders NOTHING without `eway.record`, nothing while the
        * notifications module is off, and nothing when the queue is empty — so
        * on an ordinary day this line contributes no pixels at all. The CA lands
        * on this screen (`routes/landing.ts`), which is why the standing job she
        * is responsible for meets her at the top of it.
        */}
      <EwayPending />

      {/*
        * THE PERIOD SELECTOR — three presets and no more. §11.2 names exactly
        * these three, and a fourth would be a spec change rather than a
        * convenience. The chosen one goes in the URL, so the page survives a
        * refresh and can be sent from the desktop to the phone.
        *
        * REAL BUTTONS with `aria-pressed`, not a segmented control drawn out of
        * divs: this is the control every figure below depends on, and it has to
        * be reachable by keyboard and announceable by a screen reader.
        */}
      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm font-medium">{t("reports.periodLabel")}</legend>
        <div className="flex flex-wrap gap-2">
          {REPORT_PERIOD_PRESETS.map((option) => (
            <Button
              key={option}
              type="button"
              size="sm"
              variant={option === preset ? "default" : "outline"}
              aria-pressed={option === preset}
              className="min-h-11"
              onClick={() => {
                setPreset(option)
              }}
            >
              {t(REPORT_PRESET_LABEL_KEY[option])}
            </Button>
          ))}
        </div>
        <p className="text-muted-foreground text-sm">
          {t("reports.periodHelp")}
          {summary === undefined
            ? null
            : ` ${t("reports.dateRange", {
                from: formatDay(summary.period.from),
                to: formatDay(summary.period.to),
              })}`}
        </p>
      </fieldset>

      {summaryQuery.isError ? (
        <Alert variant="destructive">
          <AlertTitle>{t("err.couldNotLoad")}</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-3">
            <span>
              {summaryQuery.error instanceof ApiError
                ? summaryQuery.error.message
                : t("err.somethingWentWrong")}
            </span>
            <Button
              variant="outline"
              onClick={() => {
                void summaryQuery.refetch()
              }}
            >
              {t("btn.retry")}
            </Button>
          </AlertDescription>
        </Alert>
      ) : (
        <>
          <HeadlineFigures summary={summary} loading={summaryQuery.isPending} />
          <FiguresFootnotes summary={summary} />

          {/*
            * "WHO DO I PHONE THIS MORNING" — newly possible now that §17.5's
            * ledger exists, and the most actionable thing in the app. It renders
            * NOTHING when the payments module is off (a 404, §18.3), when the
            * caller lacks `payment.view`, or when nothing is owed.
            */}
          {ageingQuery.isError && isFeatureOff(ageingQuery.error) ? null : !canSeePayments ? null : (
            <ChartOutstandingByAge
              data={ageingQuery.data}
              loading={ageingQuery.isPending && !ageingQuery.isError}
            />
          )}

          <ChartTopCustomers
            rows={customersQuery.data?.data}
            loading={customersQuery.isPending}
            from={customersQuery.data?.period.from ?? ""}
            to={customersQuery.data?.period.to ?? ""}
          />

          {/*
            * THE HOME SCREEN'S CHART, REUSED RATHER THAN REDRAWN — same
            * component, same look, same twelve tappable months — showing the
            * financial year the CHOSEN PERIOD falls in, which in April is not
            * necessarily the current one.
            *
            * ITS COLUMNS ARE GROSS OF CREDIT NOTES and the line beneath says so.
            * The series comes from `/dashboard/summary`, which sums invoices
            * only, while the cards above are net (§17.3). Two different
            * questions; the caption names which, rather than letting the chart
            * quietly disagree with the card above it.
            */}
          <div className="flex flex-col gap-2">
            <ChartBilledByMonth
              summary={monthlyQuery.data}
              loading={monthlyQuery.isPending && summary !== undefined}
              className=""
            />
            {monthlyQuery.data === undefined ? null : (
              <p className="text-muted-foreground text-sm">{t("reports.monthlyGrossNote")}</p>
            )}
          </div>

          <ReportCustomerTable data={customersQuery.data} loading={customersQuery.isPending} />

          <ReportExportCard
            preset={preset}
            from={summary?.period.from ?? ""}
            to={summary?.period.to ?? ""}
            loading={summary === undefined}
          />
        </>
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* The three figures §11.2 names, plus the one §17.5 added                     */
/* -------------------------------------------------------------------------- */

function HeadlineFigures({
  summary,
  loading,
}: {
  summary: ReportSummaryResponse | undefined
  loading: boolean
}): React.JSX.Element {
  const { t } = useTranslation()

  const grid =
    "*:data-[slot=card]:from-primary/5 *:data-[slot=card]:to-card grid grid-cols-1 gap-4 *:data-[slot=card]:bg-gradient-to-t *:data-[slot=card]:shadow-xs @xl/main:grid-cols-2 @5xl/main:grid-cols-3"

  if (loading || summary === undefined) {
    return (
      <div className={grid} data-testid="report-headline">
        {[0, 1, 2].map((index) => (
          <Card key={index} className="@container/card">
            <CardHeader>
              <CardDescription>
                <Skeleton className="h-4 w-28" />
              </CardDescription>
              <CardTitle>
                <Skeleton className="mt-1 h-8 w-32" />
              </CardTitle>
            </CardHeader>
          </Card>
        ))}
      </div>
    )
  }

  const period = summary.period

  return (
    <div className={grid} data-testid="report-headline">
      {/*
        * TOTAL BILLED — `net.grandTotalPaise`, which is the bills LESS the credit
        * notes dated in this period. Never `billed.grandTotalPaise`: that block
        * is gross and showing it alone would over-state the liability the owner
        * is about to declare.
        */}
      <Card className="@container/card">
        <CardHeader>
          <CardDescription>{t("reports.totalBilled")}</CardDescription>
          <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
            {formatRupees(summary.net.grandTotalPaise)}
          </CardTitle>
          <CardAction>
            <Badge variant="outline">
              {t("reports.customerBills", { count: summary.billed.count })}
            </Badge>
          </CardAction>
        </CardHeader>
        <CardFooter className="flex-col items-start gap-1.5 text-sm">
          <Link
            to={routes.invoicesInPeriod(period.from, period.to)}
            className="line-clamp-1 flex gap-2 font-medium hover:underline"
          >
            {t("reports.seeBills")} <ArrowRightIcon className="size-4" />
          </Link>
          <div className="text-muted-foreground">{t("reports.totalBilledHelp")}</div>
        </CardFooter>
      </Card>

      {/*
        * TAX ON THESE BILLS. §11.2's own phrase is "tax collected" and this
        * caption deliberately is not: COLLECTED is a claim about money arriving,
        * and the receipts card beside it is the figure that makes that claim.
        * Two questions, two captions, never one word for both.
        */}
      <Card className="@container/card">
        <CardHeader>
          <CardDescription>{t("reports.taxCharged")}</CardDescription>
          <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
            {formatRupees(summary.net.totalTaxPaise)}
          </CardTitle>
        </CardHeader>
        <CardFooter className="flex-col items-start gap-1.5 text-sm">
          {/*
            * The three heads, because a return is filed under them separately and
            * because "why is IGST zero" is the question a Pune-only month
            * invites. Each is netted the same way the total is.
            */}
          <div className="text-muted-foreground tabular-nums">
            {t("reports.taxSplit", {
              cgst: formatRupees(summary.net.cgstPaise),
              sgst: formatRupees(summary.net.sgstPaise),
              igst: formatRupees(summary.net.igstPaise),
            })}
          </div>
          <div className="text-muted-foreground">{t("reports.taxChargedHelp")}</div>
        </CardFooter>
      </Card>

      <Card className="@container/card">
        <CardHeader>
          <CardDescription>{t("reports.invoiceCount")}</CardDescription>
          <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
            {summary.billed.count}
          </CardTitle>
        </CardHeader>
        <CardFooter className="flex-col items-start gap-1.5 text-sm">
          <Link
            to={routes.invoicesInPeriod(period.from, period.to)}
            className="line-clamp-1 flex gap-2 font-medium hover:underline"
          >
            {t("reports.seeBills")} <ArrowRightIcon className="size-4" />
          </Link>
          <div className="text-muted-foreground">{t("reports.invoiceCountHelp")}</div>
        </CardFooter>
      </Card>

      {/*
        * MONEY RECEIVED — CASH, NOT REVENUE, and a different question from every
        * other figure on this screen.
        *
        * ABSENT, NEVER ZERO, when the payments module is off or the caller lacks
        * `payment.view` — the API omits the key entirely, and "Received: ₹0"
        * would be a positive claim that nothing came in, made by a system that
        * is not recording receipts at all.
        *
        * IT INCLUDES RECEIPTS AGAINST BILLS FROM EARLIER MONTHS, because the
        * money arrived in this one. The help line says so; without it the owner
        * subtracts this from "Total billed" and gets a number that means nothing.
        */}
      {summary.received === undefined ? null : (
        <Card className="@container/card">
          <CardHeader>
            <CardDescription>{t("reports.received")}</CardDescription>
            <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
              {formatRupees(summary.received.totalPaise)}
            </CardTitle>
            <CardAction>
              <Badge variant="outline">
                {t("reports.receiptCount", { count: summary.received.count })}
              </Badge>
            </CardAction>
          </CardHeader>
          <CardFooter className="flex-col items-start gap-1.5 text-sm">
            <div className="text-muted-foreground">{t("reports.receivedHelp")}</div>
          </CardFooter>
        </Card>
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* What the totals do NOT contain                                              */
/* -------------------------------------------------------------------------- */

/**
 * The sentences that stop the figures above being misread.
 *
 * EACH ONE APPEARS ONLY WHEN IT IS TRUE. A permanent row of "0 cancelled bills,
 * 0 credit notes, no discount" is furniture the eye learns to skip, and then the
 * month one of them is non-zero it gets skipped too. On an ordinary month this
 * block is empty and the screen is shorter.
 */
function FiguresFootnotes({
  summary,
}: {
  summary: ReportSummaryResponse | undefined
}): React.JSX.Element | null {
  const { t } = useTranslation()

  if (summary === undefined) return null

  const notes: string[] = []

  // Credit notes: the reason "billed" here can be smaller than the bills, and
  // the reason it can go negative. The basis is stated because it is surprising.
  if (summary.credited.count > 0) {
    notes.push(
      t("reports.creditedNote", {
        count: summary.credited.count,
        amount: formatRupees(summary.credited.grandTotalPaise),
      }),
    )
    notes.push(t("reports.creditedBasis"))
  }

  if (summary.net.grandTotalPaise < 0) notes.push(t("reports.netNegativeNote"))

  // Consumed serials that contribute nothing — the CA needs the gap in the
  // number series explained, and the register he is sent will carry these rows.
  if (summary.excluded.cancelledInvoiceCount > 0) {
    notes.push(t("reports.cancelledNote", { count: summary.excluded.cancelledInvoiceCount }))
  }
  if (summary.excluded.draftInvoiceCount > 0) {
    notes.push(t("reports.draftNote", { count: summary.excluded.draftInvoiceCount }))
  }

  // §15(3): already off the taxable value. Shown so "how much did I give away
  // this month" has an answer, and never subtracted again.
  if (summary.discounts.totalPaise > 0) {
    notes.push(
      t("reports.discountNote", { amount: formatRupees(summary.discounts.totalPaise) }),
    )
  }

  // Buyers with no GSTIN: they are in the register, but the GSTR-1 B2B table
  // cannot carry them. Better known before filing than during it.
  if (summary.b2c.invoiceCount > 0) {
    notes.push(
      t("reports.b2cNote", {
        count: summary.b2c.invoiceCount,
        amount: formatRupees(summary.b2c.grandTotalPaise),
      }),
    )
  }

  if (notes.length === 0) return null

  return (
    <Card>
      <CardContent className="text-muted-foreground flex flex-col gap-2 text-sm">
        {notes.map((note) => (
          <p key={note}>{note}</p>
        ))}
      </CardContent>
    </Card>
  )
}
